import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Apple, Loader2, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import api, { formatApiError } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function WalletButtons({ profileId }) {
  const { t } = useI18n();
  const [elig, setElig] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get(`/company/profiles/${profileId}/wallet-eligibility`)
      .then(({ data }) => setElig(data))
      .catch(() => setElig(false));
  }, [profileId]);

  if (elig === null || elig === false) return null;
  if (elig.state === "locked") return null;

  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isDesktop = !isIOS && !isAndroid;
  const showApple = isIOS || isDesktop;
  const showGoogle = isAndroid || isDesktop;

  const addGoogle = async () => {
    if (!elig.google_configured) return;
    setBusy("google");
    try {
      const { data } = await api.post(`/company/profiles/${profileId}/google-wallet`);
      window.location.assign(data.save_url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("dash.walletError"));
      setBusy(null);
    }
  };

  const addApple = () => {
    if (!elig.apple_configured) return;
    window.location.href = `${BACKEND_URL}/api/company/profiles/${profileId}/apple-wallet`;
  };

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-[#0d0d14] p-6" data-testid="wallet-section">
      <div className="flex items-center gap-2">
        <Wallet className="w-4 h-4 text-cyan-400" />
        <h3 className="font-display font-semibold text-white">{t("dash.walletTitle")}</h3>
      </div>

      {elig.state === "trial_teaser" ? (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-3.5 text-sm text-cyan-200" data-testid="wallet-teaser">
          <Sparkles className="w-5 h-5 shrink-0 mt-0.5 text-cyan-400" />
          <p className="leading-relaxed">{t("dash.walletTeaser")}</p>
        </div>
      ) : (
        <>
          <p className="mt-2 text-sm text-zinc-400">{t("dash.walletDesc")}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {showApple && (
              <button type="button" onClick={addApple} disabled={!elig.apple_configured} data-testid="wallet-apple-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-white text-black text-sm font-semibold px-5 py-3 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-200 transition-colors duration-300">
                <Apple className="w-4 h-4" />
                {elig.apple_configured ? t("dash.walletApple") : `${t("dash.walletApple")} — ${t("dash.walletSoon")}`}
              </button>
            )}
            {showGoogle && (
              <button type="button" onClick={addGoogle} disabled={!elig.google_configured || busy === "google"} data-testid="wallet-google-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-gradient text-white text-sm font-semibold px-5 py-3 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-300">
                {busy === "google" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
                {elig.google_configured ? t("dash.walletGoogle") : `${t("dash.walletGoogle")} — ${t("dash.walletSoon")}`}
              </button>
            )}
          </div>
          {isDesktop && <p className="mt-3 text-xs text-zinc-500">{t("dash.walletDesktopHint")}</p>}
        </>
      )}
    </div>
  );
}
